/**
 * Convert string to title case
 * @param {string} str - String to convert
 * @returns {string} Title cased string
 */
export const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return str;

  // Handle special cases for role names and common patterns
  const specialCases = {
    qa_engineer: "QA Engineer",
    "qa-engineer": "QA Engineer",
    project_manager: "Project Manager",
    "project-manager": "Project Manager",
    test_manager: "Test Manager",
    "test-manager": "Test Manager",
    team_lead: "Team Lead",
    "team-lead": "Team Lead",
    lead: "Lead",
    admin: "Admin",
    tester: "Tester",
    "chief-admin": "Chief Admin",
    chief_admin: "Chief Admin",
    qa: "QA",
    dev: "Dev",
  };

  const lowerStr = str.toLowerCase();
  if (specialCases[lowerStr]) {
    return specialCases[lowerStr];
  }

  // Convert underscore and hyphen separated words
  return str
    .toLowerCase()
    .split(/[\s_-]+/)
    .map((word) => {
      // Don't capitalize common words unless they're the first word
      if (word.length <= 2) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

/**
 * Format email for display (keep lowercase but can add title case for name part)
 * @param {string} email - Email address
 * @returns {string} Formatted email
 */
export const formatEmail = (email) => {
  if (!email) return email;
  // Email domain should stay lowercase, but we can capitalize the local part if needed
  return email.toLowerCase();
};

/**
 * Format name for display (title case)
 * @param {string|object} name - Name to format (string or user object with full_name)
 * @returns {string} Formatted name
 */
export const formatName = (name) => {
  if (!name) return name;
  // API may return created_by/updated_by as user objects - extract full_name
  const str =
    typeof name === "object" ? name.full_name || name.name || "" : String(name);
  const result = toTitleCase(str);
  return typeof result === "string" ? result : "";
};

/**
 * Format role for display (preserve original case)
 * @param {string} role - Role to format
 * @returns {string} Formatted role
 */
export const formatRole = (role) => {
  if (!role) return role;
  // Return role name as-is to preserve the exact case from the database
  return role;
};

/**
 * Format department for display (title case)
 * @param {string} department - Department to format
 * @returns {string} Formatted department
 */
export const formatDepartment = (department) => {
  if (!department) return department;
  const deptMap = {
    qa: "QA",
    product: "Product",
    dev: "Dev",
    support: "Support",
  };
  return deptMap[department.toLowerCase()] || toTitleCase(department);
};

/** Server-formatted client wall time: yyyy-MM-dd HH:mm:ss, no offset/milliseconds. */
const SERVER_WALL_DATETIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

/**
 * Raw API datetime for tables when backend already applied client timezone.
 * @param {unknown} value
 * @returns {string}
 */
export const displayApiDateTime = (value) => {
  if (value == null || value === "") return "—";
  const s = String(value).trim();
  return s.replace("T", " ");
};

/**
 * Format date for display (dd/mm/yyyy) - consistent across the application
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date or '-' if invalid
 */
export const formatDate = (dateString) => {
  if (!dateString) return "-";
  if (typeof dateString === "string") {
    const t = dateString.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
};

/**
 * Format date and time for display. Server wall-time values are shown as returned;
 * true ISO instants still fall back to browser-local formatting.
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date-time or '-' if invalid
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  if (typeof dateString === "string") {
    const t = dateString.trim();
    if (SERVER_WALL_DATETIME.test(t)) {
      return displayApiDateTime(t);
    }
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

/**
 * Format date and time for client listing: DD-MM-YYYY H:M:S (e.g. 21-02-2026 17:55:35)
 * Use with label "Created At: " (space after "At:") for display.
 * Server wall-time values are shown as-is.
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date-time or '-' if invalid
 */
export const formatCreatedAtListing = (dateString) => {
  if (!dateString) return "-";
  if (typeof dateString === "string") {
    const t = dateString.trim();
    if (SERVER_WALL_DATETIME.test(t)) {
      return displayApiDateTime(t);
    }
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

/**
 * Generate user initials from full name
 * @param {string} fullName - User's full name
 * @returns {string} Initials (e.g., "JD" for "John Doe" or "J" for "John")
 */
export const getInitials = (fullName) => {
  if (!fullName || typeof fullName !== "string") return "U";

  const trimmed = fullName.trim();
  if (!trimmed) return "U";

  const parts = trimmed.split(/\s+/).filter((part) => part.length > 0);

  if (parts.length === 0) return "U";

  if (parts.length === 1) {
    // Only first name available, return first letter
    return parts[0].charAt(0).toUpperCase();
  }

  // First letter of first name and first letter of last name
  const firstInitial = parts[0].charAt(0).toUpperCase();
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();

  return `${firstInitial}${lastInitial}`;
};
